import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MagnifyingGlass, Barcode, Trash, X, Plus, ArrowLeft,
  User, Receipt, CashRegister, Download,
  UserPlus, ClockCounterClockwise,
  SignOut,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { fmtARS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getEffectiveListaId, getListaPosDefault } from "@/lib/effectiveListaPrecio";
import { canAccessRoute, getSecondaryRoute } from "@/lib/permissions";
import { imprimirTicketDelivery, imprimirTicketFiscal } from "@/lib/printTicket80";
import {
  DeliveryMostradorView, RubroGrid, ProductGrid, NumericKeypad,
  PaymentDialog, QuickClienteDialog, CierreCajaDialog, CajaDiariaView,
} from "./pos/PosComponents";
import { checkListaIncorrecta } from "@/lib/priceListCheck";
import AlertaListaPrecioModal from "@/components/AlertaListaPrecioModal";
import AlertaExcesoKgTNModal from "@/components/AlertaExcesoKgTNModal";

// ============================================================================
// PUNTO DE VENTA (POS) — Estética LA BIFERIA
// Layout: panel izq. (carrito + acciones) + panel der. (rubros + productos)
// Paleta: rojo bordó #6B1F24 / rojo bandera #921A0E / crema #F9F6F6 / verde #4DC247
// ============================================================================

const DEFAULT_RUBRO_ID = "__INICIO__";

export default function POSPage() {
  // Data
  const [rubros, setRubros] = useState([]);
  const [productos, setProductos] = useState([]);
  const [listas, setListas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [mediosPago, setMediosPago] = useState([]);
  // Toggle "Pago en efectivo" — dispara la aplicación del descuento por efectivo (promociones)
  const [pagoEfectivoOn, setPagoEfectivoOn] = useState(false);
  // Diálogo de solo lectura para ver promociones vigentes desde el POS
  const [showPromosDialog, setShowPromosDialog] = useState(false);
  // Toggle "Descuento 5%" (cortesía) — se aplica al total independientemente del motor de promociones
  const [descuento5On, setDescuento5On] = useState(false);

  // caja_id del medio "Efectivo" (para enviar como medio_pago al motor de promociones)
  // Se toma primero de los medios de pago del usuario (POS), y si no hay, del listado de cajas.
  const cajaEfectivoId = useMemo(() => {
    const matchEfectivo = (nombre, tipo) => {
      const t = (tipo || "").toLowerCase();
      const n = (nombre || "").toLowerCase();
      return t === "efectivo" || n.includes("efectivo") || n.includes("efvo");
    };
    const desdeMedios = mediosPago.find((m) => matchEfectivo(m.nombre, m.tipo));
    if (desdeMedios) return desdeMedios.caja_id || desdeMedios.caja?.id || null;
    const desdeCajas = cajas.find((c) => matchEfectivo(c.nombre, c.tipo));
    return desdeCajas?.id || null;
  }, [mediosPago, cajas]);

  // Sesión de caja (obligatoria para vender)
  const [sesion, setSesion] = useState(null);
  const [sesionLoading, setSesionLoading] = useState(true);

  const loadSesion = useCallback(async () => {
    try {
      const { data } = await api.get("/pos/sesion-actual");
      setSesion(data);
    } catch (err) {
      setSesion({ active: false });
    } finally {
      setSesionLoading(false);
    }
  }, []);

  useEffect(() => { loadSesion(); }, [loadSesion]);

  // Estado UI
  const [selectedRubroId, setSelectedRubroId] = useState(DEFAULT_RUBRO_ID);
  const [tab, setTab] = useState("mostrador"); // mostrador | delivery
  const [deliveryPedidos, setDeliveryPedidos] = useState([]);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [pedidoDeliveryOrigen, setPedidoDeliveryOrigen] = useState(null);

  // Set con IDs de pedidos "conocidos" (se usa para detectar nuevos y auto-imprimir)
  const knownDeliveryIdsRef = useRef(null); // null=aún no inicializado
  const printingQueueRef = useRef(Promise.resolve());

  // Configuración de auto-impresión (hora umbral para pedidos con fecha_envio futura).
  // Se refresca del backend en el mount y cuando se cambia desde Admin → Etiquetas.
  const autoprintConfigRef = useRef({ enabled: true, hora: 17 });
  const barcodeBalanzaRef = useRef({
    enabled: true,
    variants: [{
      id: "default", nombre: "Por peso (gramos)",
      prefix: "2", suffix: "", length: 13,
      codigo_start: 1, codigo_len: 6, peso_start: 7, peso_len: 6,
      peso_divisor: 1000, strip_leading_zeros: true,
    }],
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: ap }, { data: bb }] = await Promise.all([
          api.get("/pos/config/auto-print").catch(() => ({ data: null })),
          api.get("/pos/config/barcode-balanza").catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          if (ap) {
            autoprintConfigRef.current = {
              enabled: !!ap.enabled,
              hora: Number.isFinite(+ap.hora) ? +ap.hora : 17,
            };
          }
          if (bb) barcodeBalanzaRef.current = { ...barcodeBalanzaRef.current, ...bb };
        }
      } catch (_) { /* mantiene defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadDelivery = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setDeliveryLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowISO = tomorrow.toISOString().slice(0, 10);
      const { data } = await api.get("/pos/delivery", { params: { fecha: today, fecha_hasta: tomorrowISO } });
      const next = data || [];

      // Detectar pedidos nuevos por (origen+id) y auto-imprimir
      const nextIds = new Set(next.map((p) => `${p.origen}-${p.id}`));
      const known = knownDeliveryIdsRef.current;
      // Helper: sólo se imprime automáticamente si el pedido corresponde a HOY
      // (o si ya son las ≥autoprintHora y el pedido era para mañana o hoy sin imprimir).
      // Para pedidos programados a MAÑANA se deja pendiente hasta la ventana horaria.
      const autoprintCfg = autoprintConfigRef.current || { enabled: true, hora: 17 };
      const AUTOPRINT_HOUR = Number.isFinite(+autoprintCfg.hora) ? +autoprintCfg.hora : 17;
      const AUTOPRINT_ENABLED = autoprintCfg.enabled !== false;
      const shouldAutoPrint = (ped) => {
        if (!AUTOPRINT_ENABLED) return false;
        if (ped.ticket_impreso_at) return false; // ya salió, no imprimir de nuevo
        if (ped.fecha_envio === today) return true;
        // fecha_envio futura: sólo imprimir si ya son ≥AUTOPRINT_HOUR
        const now = new Date();
        return now.getHours() >= AUTOPRINT_HOUR;
      };
      const enqueueAutoPrint = (peds) => {
        peds.forEach((ped) => {
          printingQueueRef.current = printingQueueRef.current.then(async () => {
            try {
              await imprimirTicketDelivery(ped);
              // Persistir en backend que ya se imprimió (auto).
              try {
                await api.post("/pos/delivery/marcar-impreso", { origen: ped.origen, pedido_id: ped.id });
              } catch (_) { /* no critical */ }
            } catch (e) {
              console.error("[auto-print] falló ticket", ped?.numero, e);
            }
            await new Promise((r) => setTimeout(r, 1500));
          });
        });
      };
      if (known === null) {
        // Primera carga: no imprimir nada, sólo memorizar lo existente
        knownDeliveryIdsRef.current = nextIds;
      } else {
        const nuevos = next.filter((p) => !known.has(`${p.origen}-${p.id}`));
        if (nuevos.length > 0) {
          const imprimir = nuevos.filter(shouldAutoPrint);
          const diferidos = nuevos.filter((p) => !shouldAutoPrint(p) && !p.ticket_impreso_at);
          if (imprimir.length > 0) {
            try {
              toast.success(`${imprimir.length} pedido${imprimir.length !== 1 ? "s" : ""} nuevo${imprimir.length !== 1 ? "s" : ""} · imprimiendo…`);
            } catch (_) {}
            enqueueAutoPrint(imprimir);
          }
          if (diferidos.length > 0) {
            try {
              toast.info(`${diferidos.length} pedido${diferidos.length !== 1 ? "s" : ""} para otra fecha · impresión diferida a las ${String(AUTOPRINT_HOUR).padStart(2, "0")}h`);
            } catch (_) {}
          }
        }
        knownDeliveryIdsRef.current = nextIds;

        // Detectar pedidos DIFERIDOS que ya cruzaron la ventana horaria y
        // aún no fueron impresos manualmente: encolarlos ahora.
        const nowHour = new Date().getHours();
        if (AUTOPRINT_ENABLED && nowHour >= AUTOPRINT_HOUR) {
          const pendientes = next.filter((p) => !p.ticket_impreso_at && p.fecha_envio !== today);
          if (pendientes.length > 0) enqueueAutoPrint(pendientes);
        }
      }

      setDeliveryPedidos(next);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "No se pudo cargar la lista Delivery");
    } finally {
      if (!silent) setDeliveryLoading(false);
    }
  }, []);

  // Auto-refresh del contador de Delivery cada 15s (para detectar nuevos y auto-imprimir)
  useEffect(() => {
    loadDelivery({ silent: true });
    const iv = setInterval(() => loadDelivery({ silent: true }), 15000);
    return () => clearInterval(iv);
  }, [loadDelivery]);

  // Impresión manual: envuelve `imprimirTicketDelivery` con la marca de "ya impreso"
  // para que el badge del botón muestre "yaimpreso" y no reimprima solo.
  const imprimirTicketConMarca = useCallback(async (ped) => {
    try {
      await imprimirTicketDelivery(ped);
    } finally {
      try {
        await api.post("/pos/delivery/marcar-impreso", { origen: ped.origen, pedido_id: ped.id });
        loadDelivery({ silent: true });
      } catch (_) { /* no critical */ }
    }
  }, [loadDelivery]);

  // Marcar estado / kg reales de un ítem del pedido en Mostrador
  const marcarItemMostrador = useCallback(async ({ origen, pedido_id, item_idx, estado = null, kg_reales = null }) => {
    try {
      const { data } = await api.post("/pos/delivery/marcar-item", {
        origen, pedido_id, item_idx, estado, kg_reales,
      });
      // Refresco silencioso (evita reset del scroll)
      loadDelivery({ silent: true });
      if (data?.nuevo_estado_local === "listo_para_facturar") {
        toast.success("Pedido pasado a LISTO PARA FACTURAR ✓");
      }
      return data;
    } catch (e) {
      // Guard TN de kg excedidos → mostrar modal grande centrado en vez de toast.
      // No re-throw en este caso: el error ya está manejado visualmente y no
      // queremos que React dev overlay lo capture como uncaught.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "tn_kg_excedidos") {
        setExcesoKgAlerta(detail);
        return null;
      }
      toast.error((typeof detail === "string" ? detail : detail?.message) || "No se pudo actualizar el ítem");
      throw e;
    }
  }, [loadDelivery]);

  // Pedido seleccionado en Delivery / Mostrador para direccionar los escaneos de balanza
  const [mostradorSelected, setMostradorSelected] = useState(null); // {origen, id}
  const mostradorSelectedRef = useRef(null);
  useEffect(() => { mostradorSelectedRef.current = mostradorSelected; }, [mostradorSelected]);
  const deliveryPedidosRef = useRef([]);
  useEffect(() => { deliveryPedidosRef.current = deliveryPedidos; }, [deliveryPedidos]);
  const tabRef = useRef(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);

  const [articleSearch, setArticleSearch] = useState("");
  const [cliente, setCliente] = useState(null); // objeto {id, nombre, cuit, condicion_iva, lista_precios_id}
  const [listaId, setListaId] = useState("");
  // Lista de precios que el POS toma como default (setting global editable
  // por admin). Se persiste en `db.settings.pos_lista_precios_id`. La listaId
  // arriba puede diferir puntualmente si el operador cambió la lista para la
  // venta actual; al terminar/limpiar la venta, vuelve a este default.
  const [posListaDefaultId, setPosListaDefaultId] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [items, setItems] = useState([]); // [{producto_id, codigo, descripcion, kilos, cantidad, precio_unitario, iva_pct}]

  // Dialogs
  const [keypadProd, setKeypadProd] = useState(null); // producto seleccionado para ingresar cantidad/kilos
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanValue, setScanValue] = useState("");
  const [clienteSearch, setClienteSearch] = useState("");
  const [clienteDialogOpen, setClienteDialogOpen] = useState(false);
  const [payDialog, setPayDialog] = useState(null); // "cobrar" | "facturar_cobrar" | null
  const [processing, setProcessing] = useState(false);
  // Alerta previa al cierre de venta si algún ítem no matchea la lista del cliente.
  // `listaAlerta` = { mismatches, mode, pagos, extra } — se dispara desde submitVenta,
  // el usuario elige "Modificar venta" (cancela) o "Facturar igual" (bypass=true).
  const [listaAlerta, setListaAlerta] = useState(null);
  // Modal grande centrado para cuando un pedido TN intenta cargar más kg que
  // los pedidos por el cliente. Data viene del 409 del backend con code
  // "tn_kg_excedidos".
  const [excesoKgAlerta, setExcesoKgAlerta] = useState(null);
  const [lastSale, setLastSale] = useState(null); // resultado tras venta OK
  const [quickClienteOpen, setQuickClienteOpen] = useState(false);
  const [cierreOpen, setCierreOpen] = useState(false);

  // Ventas en múltiples pestañas — orden fijo por creación
  const [parked, setParked] = useState(() => {
    try {
      const saved = localStorage.getItem("pos_parked_sales");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [tabOrder, setTabOrder] = useState(() => {
    try {
      const saved = localStorage.getItem("pos_tab_order");
      if (saved) return JSON.parse(saved);
    } catch {}
    return [];
  });
  const [activeSaleId, setActiveSaleId] = useState(() => `tab-${Date.now()}`);

  // Vista superior del POS: "ventas" (operativa) o "caja" (Caja Diaria)
  const [topView, setTopView] = useState("ventas");

  // Asegurar que el activeSaleId siempre esté en tabOrder (idempotente)
  useEffect(() => {
    setTabOrder((prev) => {
      if (prev.includes(activeSaleId)) return prev;
      // Agregar al final si no existe (nueva pestaña abierta)
      return [...prev, activeSaleId];
    });
  }, [activeSaleId]);

  // Promociones (auto-aplicadas)
  const [promosAplicables, setPromosAplicables] = useState([]);
  const [promosDescartadas, setPromosDescartadas] = useState(new Set());

  const nav = useNavigate();
  const { user, logout } = useAuth();
  // "Salir" del POS: si el usuario tiene otros módulos habilitados (o es
  // admin), vamos al ERP; sino, cerramos sesión. `getSecondaryRoute` calcula
  // el mejor destino distinto a /pos priorizando dashboard > ventas > ...
  const backTarget = user?.is_admin
    ? "/dashboard"
    : getSecondaryRoute(user, "pos");
  const canGoBack = !!backTarget;
  const doExit = async () => {
    if (canGoBack) {
      nav(backTarget);
    } else {
      await logout();
      nav("/login", { replace: true });
    }
  };
  const articleSearchRef = useRef(null);

  // ---------- Load ----------
  const load = useCallback(async () => {
    try {
      const [{ data: rb }, { data: pr }, { data: ls }, { data: cl }, { data: cj }, { data: mp }] = await Promise.all([
        api.get("/rubros"),
        api.get("/productos"),
        api.get("/listas-precios"),
        api.get("/clientes"),
        api.get("/cajas"),
        api.get("/medios-pago/mios", { params: { scope: "pos" } }).catch(() => ({ data: [] })),
      ]);
      setRubros((rb || []).filter((r) => r.activo !== false));
      setProductos(pr || []);
      const ordenadas = (ls || []).sort((a, b) => (a.orden || 0) - (b.orden || 0));
      setListas(ordenadas);
      setClientes(cl || []);
      setCajas((cj || []).filter((c) => c.tipo !== "cheques"));
      setMediosPago(mp || []);

      // Setting global / Flag: lista de precios que el POS usa por defecto.
      let posDefaultId = "";
      try {
        const { data: cfg } = await api.get("/config/pos-lista");
        posDefaultId = cfg?.lista_precios_id || "";
      } catch { /* si falla el endpoint, seguimos con flag es_lista_pos */ }
      setPosListaDefaultId(posDefaultId);
      const listaPos = ordenadas.find((l) => l && l.es_lista_pos === true) ||
        (posDefaultId ? (ordenadas.find((l) => l.id === posDefaultId) || null) : null);

      // Cliente por defecto = Consumidor Final
      const cf = (cl || []).find((c) =>
        (c.nombre || "").toLowerCase().trim() === "consumidor final" ||
        c.tipo_documento === "CF"
      );
      if (cf) setCliente(cf);
      // Regla: en POS SIEMPRE arrancamos con la lista POS (flag es_lista_pos o setting).
      // Ignora la lista del cliente porque el POS es mostrador (minorista).
      if (listaPos) setListaId(listaPos.id);
      else if (ordenadas[0]) setListaId(ordenadas[0].id);
    } catch (err) {
      toast.error("Error al cargar datos");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---------- Filtros ----------
  const filtered = useMemo(() => {
    let arr = productos;
    if (selectedRubroId !== DEFAULT_RUBRO_ID) {
      arr = arr.filter((p) => p.rubro_id === selectedRubroId);
    }
    if (articleSearch.trim()) {
      const q = articleSearch.toLowerCase();
      arr = arr.filter((p) =>
        (p.nombre || "").toLowerCase().includes(q) ||
        (p.codigo || "").toLowerCase().includes(q)
      );
    }
    return arr.slice(0, 60); // límite visual
  }, [productos, selectedRubroId, articleSearch]);

  const priceOf = useCallback((p, lid = listaId) => {
    const precios = p.precios || {};
    // El precio de la lista YA INCLUYE IVA (el "Total" que ve el cliente).
    // El backend calcula el neto dividiendo por (1 + iva_pct/100).
    // BUG FIX: nunca caer silenciosamente a otra lista de precios (evita mezclar "Minorista"
    // en clientes mayoristas). Fallback: precio_actual sólo si la lista no tiene precio.
    const listPrice = lid ? precios[lid] : null;
    return (listPrice && listPrice > 0) ? listPrice : (p.precio_actual || 0);
  }, [listaId]);

  // ---------- Cart operations ----------

  const addFromProd = (p, kilos, cantidad = 1) => {
    const precio = priceOf(p);
    const it = {
      producto_id: p.id,
      codigo: p.codigo,
      descripcion: p.nombre,
      unidad: p.unidad || "kg",
      kilos: parseFloat(kilos || 0),
      cantidad: parseFloat(cantidad || 1),
      precio_unitario: precio,
      iva_pct: p.iva_pct != null ? p.iva_pct : 10.5,
    };
    setItems((prev) => [...prev, it]);
  };

  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const clearCart = () => {
    if (items.length && !window.confirm("¿Vaciar el carrito?")) return;
    setItems([]);
    setObservaciones("");
    setActiveSaleId(null);
    setPromosDescartadas(new Set());
    // Si estaba vinculado a un pedido de Delivery, desvincular — el pedido
    // permanece visible en Delivery hasta que se facture.
    setPedidoDeliveryOrigen(null);
  };

  // Doble-click sobre línea del carrito → editar kilos/cantidad con keypad
  const openKeypadForRow = (rowIdx) => {
    const it = items[rowIdx];
    if (!it) return;
    const p = productos.find((x) => x.id === it.producto_id) || {
      id: it.producto_id, codigo: it.codigo, nombre: it.descripcion,
      unidad: it.unidad || "kg", iva_pct: it.iva_pct, precios: {},
      precio_actual: it.precio_unitario,
    };
    setKeypadProd({
      producto: p,
      editRowIdx: rowIdx,
      initialValue: (it.unidad || "kg").toLowerCase() === "kg" ? it.kilos : it.cantidad,
    });
  };

  // Doble-click sobre un producto de la grilla → reemplaza el producto de la última fila conservando kg
  const swapLastRowProduct = (newProd) => {
    // Cerrar keypad si se abrió por el click previo
    setKeypadProd(null);
    if (items.length === 0) {
      toast.info("Primero agregá un producto para poder cambiarlo con doble-click");
      openKeypad(newProd);
      return;
    }
    const idx = items.length - 1;
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const precio = priceOf(newProd);
      return {
        ...it,
        producto_id: newProd.id,
        codigo: newProd.codigo,
        descripcion: newProd.nombre,
        unidad: newProd.unidad || "kg",
        precio_unitario: precio,
        iva_pct: newProd.iva_pct != null ? newProd.iva_pct : 10.5,
      };
    }));
    toast.success(`Cambiado a ${newProd.nombre} (kilos conservados)`);
  };

  const openKeypad = (p) => setKeypadProd({ producto: p, valor: "" });

  // ---------- Totales ----------
  const totales = useMemo(() => {
    let subtotal = 0;
    let iva = 0;
    let items_count = 0;
    for (const it of items) {
      const qty = (it.unidad || "kg").toLowerCase() === "kg" ? it.kilos : it.cantidad;
      const total_civa = qty * (it.precio_unitario || 0);
      const p = it.iva_pct || 0;
      const neto = p > 0 ? total_civa / (1 + p / 100) : total_civa;
      subtotal += neto;
      iva += total_civa - neto;
      items_count += qty;
    }
    // Descuentos de promociones (importe c/IVA)
    const descPromos = (promosAplicables || [])
      .filter((p) => !promosDescartadas.has(p.promocion_id))
      .reduce((s, p) => s + Math.abs(p.importe_descuento_total || 0), 0);
    // Descuento manual del 5% (cortesía) — se aplica luego de las promociones
    const bruto = subtotal + iva;
    const baseParaDesc5 = Math.max(0, bruto - descPromos);
    const desc5 = descuento5On ? baseParaDesc5 * 0.05 : 0;
    return {
      subtotal,
      iva,
      total: Math.max(0, bruto - descPromos - desc5),
      total_bruto: bruto,
      descuento_promo: descPromos,
      descuento_5: desc5,
      items_count,
      rows: items.length,
    };
  }, [items, promosAplicables, promosDescartadas, descuento5On]);

  const promosEfectivas = useMemo(
    () => (promosAplicables || []).filter((p) => !promosDescartadas.has(p.promocion_id)),
    [promosAplicables, promosDescartadas]
  );

  // Recalcular precios de los items cuando cambia la lista de precios.
  // Esto también gatilla el useEffect de promociones aplicables.
  useEffect(() => {
    if (!listaId || items.length === 0 || !productos.length) return;
    setItems((prev) => prev.map((it) => {
      const p = productos.find((x) => x.id === it.producto_id);
      if (!p) return it;
      const nuevoPrecio = priceOf(p, listaId);
      // Solo actualizar si cambia realmente para no invalidar re-renders innecesarios
      if (Math.abs((nuevoPrecio || 0) - (it.precio_unitario || 0)) < 0.001) return it;
      const qty = (it.unidad || "kg").toLowerCase() === "kg" ? (it.kilos || 0) : (it.cantidad || 0);
      return { ...it, precio_unitario: nuevoPrecio, total: nuevoPrecio * qty };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listaId, productos.length]);

  // ---------- Detección automática de promociones ----------
  useEffect(() => {
    if (items.length === 0) {
      if (promosAplicables.length) setPromosAplicables([]);
      return;
    }
    // No dispares hasta que la lista y productos estén cargados (evita primer round con listaId="")
    if (!listaId || !productos.length) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const promoItems = items.map((it) => {
          const qty = (it.unidad || "kg").toLowerCase() === "kg" ? it.kilos : it.cantidad;
          return {
            producto_id: it.producto_id,
            precio_unitario: parseFloat(it.precio_unitario || 0),
            cantidad: qty,
            kilos: (it.unidad || "kg").toLowerCase() === "kg" ? parseFloat(it.kilos || 0) : null,
          };
        });
        const subtotalCiva = items.reduce((s, it) => {
          const qty = (it.unidad || "kg").toLowerCase() === "kg" ? it.kilos : it.cantidad;
          return s + qty * (it.precio_unitario || 0);
        }, 0);
        const { data } = await api.post("/promociones/aplicables", {
          cliente_id: cliente?.id || null,
          lista_precios_id: listaId || null,
          medio_pago: pagoEfectivoOn ? (cajaEfectivoId || "efectivo") : null,
          fecha: new Date().toISOString().substring(0, 10),
          subtotal_factura: subtotalCiva,
          items: promoItems,
        }, { signal: controller.signal });
        setPromosAplicables(data?.promos || []);
      } catch (err) {
        // silencioso
      }
    }, 400);
    return () => { clearTimeout(timer); controller.abort(); };
    // eslint-disable-next-line
  }, [items, cliente?.id, listaId, pagoEfectivoOn, cajaEfectivoId]);

  const togglePromo = (id) => {
    setPromosDescartadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ---------- Ventas en espera / pestañas (orden fijo por creación) ----------
  useEffect(() => {
    try {
      localStorage.setItem("pos_parked_sales", JSON.stringify(parked));
      localStorage.setItem("pos_tab_order", JSON.stringify(tabOrder));
    } catch {}
  }, [parked, tabOrder]);

  const parkCurrent = () => {
    if (items.length === 0) return toast.info("El carrito está vacío");
    const snapshot = {
      id: activeSaleId,
      cliente,
      listaId,
      items,
      observaciones,
      createdAt: new Date().toISOString(),
    };
    setParked((prev) => {
      const idx = prev.findIndex((s) => s.id === snapshot.id);
      if (idx >= 0) return prev.map((s, i) => (i === idx ? snapshot : s));
      return [...prev, snapshot];
    });
    setItems([]);
    setObservaciones("");
    setPromosDescartadas(new Set());
    // Nueva pestaña vacía activa (se auto-agrega al final del tabOrder via useEffect)
    setActiveSaleId(`tab-${Date.now()}`);
    toast.success(`Venta en espera · ${snapshot.items.length} ítems`);
  };

  const resumeParked = (sale) => {
    // Guardar la actual en parked con SU MISMO ID → mantiene su posición en tabOrder
    if (items.length > 0) {
      const snapshot = {
        id: activeSaleId,
        cliente,
        listaId,
        items,
        observaciones,
        createdAt: new Date().toISOString(),
      };
      setParked((prev) => {
        const idx = prev.findIndex((s) => s.id === snapshot.id);
        if (idx >= 0) return prev.map((s, i) => (i === idx ? snapshot : s));
        return [...prev, snapshot];
      });
    }
    // Cargar target en el estado top-level y sacarla de parked. tabOrder NO se toca.
    setParked((prev) => prev.filter((s) => s.id !== sale.id));
    setItems(sale.items || []);
    setCliente(sale.cliente || null);
    setListaId(sale.listaId || "");
    setObservaciones(sale.observaciones || "");
    setActiveSaleId(sale.id);
    setPromosDescartadas(new Set());
  };

  // Marcar pedido pickup ya pagado como entregado — NO genera venta POS.
  // Como todavía hay que emitir la factura fiscal, el pedido pasa a
  // `listo_para_facturar` (no a "terminado"): sale del listado Delivery
  // pero queda pendiente en el módulo de Facturación.
  const marcarEntregado = useCallback(async (pedido) => {
    if (!pedido) return;
    if (!window.confirm(`Marcar el pedido ${pedido.numero} como ENTREGADO?\n\nEl pedido ya está pagado — esta acción no genera venta en el POS y lo deja LISTO PARA FACTURAR.`)) return;
    try {
      await api.post("/pos/delivery/marcar-facturado", {
        origen: pedido.origen,
        pedido_id: pedido.id,
        motivo: "entregado",
      });
      toast.success(`Pedido ${pedido.numero} entregado — pasó a listo para facturar`);
      loadDelivery({ silent: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al marcar entregado");
    }
  }, [loadDelivery]);

  const newEmptyTab = () => {
    if (items.length > 0) {
      // Parkear la actual → parkCurrent ya asigna nueva activeSaleId
      parkCurrent();
      return;
    }
    // Si el carrito está vacío no tiene sentido abrir otra pestaña vacía
    toast.info("Ya estás en una pestaña vacía");
  };

  // ---------- Cargar pedido de Delivery al POS ----------
  // Usado sólo por pedidos de tipo "retiro en el local" (pickup): el operador
  // apreta "CARGAR AL POS" y traemos items + cliente al carrito. El pedido
  // permanece en el listado Delivery hasta que se confirma la venta en POS
  // (endpoint `/pos/delivery/marcar-facturado` disparado al facturar).
  const cargarPedidoAlPOS = useCallback((pedido) => {
    if (!pedido) return;
    // Si hay una venta activa con ítems, la parqueamos primero
    if (items.length > 0) {
      parkCurrent();
    }
    // Cliente: buscar en el catálogo local; si no existe, armar objeto mínimo
    let cli = null;
    if (pedido.cliente_id) {
      cli = clientes.find((c) => c.id === pedido.cliente_id) || null;
    }
    if (!cli && (pedido.cliente_nombre || pedido.cliente_cuit)) {
      cli = {
        id: pedido.cliente_id || null,
        nombre: pedido.cliente_nombre || "",
        cuit: pedido.cliente_cuit || "",
        condicion_iva: "Consumidor Final",
        lista_precios_id: listaId || "",
      };
    }
    if (cli) setCliente(cli);
    // Prioridad: default del USUARIO > lista del cliente. Ver /lib/effectiveListaPrecio.js
    const effId = getEffectiveListaId(cli, user);
    if (effId) setListaId(effId);
    // Items: mapear del pedido → cart POS
    const cartItems = (pedido.items || []).map((it) => {
      const prod = productos.find((p) =>
        (it.producto_id && p.id === it.producto_id) ||
        (it.codigo && p.codigo === it.codigo)
      );
      const unidad = (prod?.unidad || "kg").toLowerCase();
      const esPorKg = unidad === "kg";
      // Preferir kg_reales (los que ya se pesaron en Mostrador). Fallback a kilos/cantidad.
      const kg = esPorKg ? parseFloat(it.kg_reales || it.kilos || 0) : 0;
      const cantidad = esPorKg ? 1 : parseFloat(it.cantidad || it.quantity || 1);
      const precio = prod ? priceOf(prod, effId || listaId)
                          : parseFloat(it.precio_unitario || it.price || 0);
      return {
        producto_id: prod?.id || null,
        codigo: prod?.codigo || it.codigo || "",
        descripcion: prod?.nombre || it.descripcion || "",
        unidad: prod?.unidad || "kg",
        kilos: kg,
        cantidad,
        precio_unitario: precio,
        iva_pct: prod?.iva_pct != null ? prod.iva_pct : 10.5,
      };
    });
    setItems(cartItems);
    setObservaciones(pedido.observaciones || "");
    // Guardar link para vincular al finalizar la venta
    setPedidoDeliveryOrigen({ origen: pedido.origen, id: pedido.id, numero: pedido.numero });
    setActiveSaleId(`pedido-${pedido.origen}-${pedido.id}`);
    setTab("mostrador");
    toast.success(`Pedido ${pedido.numero} cargado al POS. Cobralo cuando estés listo.`);
    // eslint-disable-next-line
  }, [items, clientes, productos, listaId, parkCurrent, priceOf]);

  const dropParked = (id) => {
    if (!window.confirm("¿Descartar esta venta en espera?")) return;
    setParked((prev) => prev.filter((s) => s.id !== id));
    setTabOrder((prev) => prev.filter((tid) => tid !== id));
  };

  // ---------- Barcode scanner (Enter) ----------
  useEffect(() => {
    // Detección de scanner por VENTANA ACUMULATIVA (no por dt entre teclas):
    // - `buf` acumula todo lo tipeado desde el último "gap" (>300ms sin teclas).
    // - Cuando llega Enter, si el buf tiene 8-20 chars numéricos Y la ventana
    //   total fue < 700ms, es un scanner SÍ o SÍ (independiente del dt individual).
    // - Si además el foco estaba en un input, lo restauramos a su valor previo
    //   ANTES de la ráfaga, para no dejar basura ni pisar kilos_sugeridos.
    // Este enfoque arregla los 2 sub-bugs anteriores:
    //   (a) la primera tecla nunca activaba scannerMode (dt vs lastAt viejo).
    //   (b) scanners con 30-50ms de intervalo entre teclas.
    let buf = "";
    let firstKeyAt = 0;
    let lastAt = 0;
    let inputAtStart = null;   // { el, valor } — restaurar si detectamos scanner
    const isEditable = (el) => {
      if (!el) return false;
      const t = (el.tagName || "").toUpperCase();
      return t === "INPUT" || t === "TEXTAREA" || t === "SELECT" || el.isContentEditable;
    };
    const resetBuf = () => { buf = ""; firstKeyAt = 0; inputAtStart = null; };
    const onKey = (e) => {
      const now = Date.now();
      const dt = now - lastAt;
      lastAt = now;

      // Gap grande → reinicio de ventana. Registro el estado del input focado ANTES
      // de que empiece la posible ráfaga (para restaurarlo si resulta ser scanner).
      if (dt > 300) {
        resetBuf();
        firstKeyAt = now;
        const act = document.activeElement;
        if (isEditable(act) && act !== articleSearchRef.current) {
          inputAtStart = { el: act, valor: act.value };
        }
      }

      if (e.key === "Enter") {
        const ventanaMs = now - firstKeyAt;
        // Es scanner si acumuló entre 8 y 20 chars numéricos en <700 ms
        const esScanner = /^\d{8,20}$/.test(buf) && ventanaMs < 700;
        if (esScanner) {
          // Preventamos el Enter (evita blur/submit del input) y restauramos
          // el valor previo si escribió al input por error.
          e.preventDefault();
          e.stopPropagation();
          if (inputAtStart && inputAtStart.el) {
            try {
              const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
              setter.call(inputAtStart.el, inputAtStart.valor);
              inputAtStart.el.dispatchEvent(new Event("input", { bubbles: true }));
              inputAtStart.el.blur();
            } catch (_) {
              // best-effort
            }
          }
          handleScan(buf);
        }
        resetBuf();
        return;
      }

      if (e.key.length === 1) buf += e.key;
    };
    window.addEventListener("keydown", onKey, true); // capture=true para llegar antes que el input
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productos, listaId]);

  const handleScan = (code) => {
    const c = String(code || "").trim();
    if (!c) return;

    // 1) Intentar cada variante configurada (formato prefix + código + peso + suffix)
    const bal = barcodeBalanzaRef.current || {};
    const balEnabled = bal.enabled !== false;
    // Ordenamos por especificidad (prefijo + sufijo más largos primero)
    // para que variantes más específicas (ej. "21..3") se prueben antes que "2..").
    const variants = (Array.isArray(bal.variants) ? bal.variants : [])
      .slice()
      .sort((a, b) =>
        (String(b.prefix || "").length + String(b.suffix || "").length) -
        (String(a.prefix || "").length + String(a.suffix || "").length)
      );

    const matchVariant = (v) => {
      if (!v || !v.prefix) return null;
      if (c.length !== Number(v.length)) return null;
      if (!c.startsWith(String(v.prefix))) return null;
      const sfx = String(v.suffix || "");
      if (sfx && !c.endsWith(sfx)) return null;
      if (!/^\d+$/.test(c)) return null;
      const codigoStr = c.substr(Number(v.codigo_start), Number(v.codigo_len));
      const pesoStr = c.substr(Number(v.peso_start), Number(v.peso_len));
      const kg = Number(pesoStr) / Number(v.peso_divisor || 1000);
      if (!Number.isFinite(kg) || kg <= 0) return null;
      const codigoLookup = v.strip_leading_zeros ? codigoStr.replace(/^0+/, "") : codigoStr;
      return { variant: v, codigoStr, pesoStr, codigoLookup, kg };
    };

    const parsed = balEnabled ? variants.map(matchVariant).find(Boolean) : null;

    if (parsed) {
      const { codigoStr, codigoLookup, kg, variant } = parsed;
      const codigoLen = Number(variant.codigo_len);

      // 1a) Si estamos en Delivery/Mostrador y hay un pedido seleccionado → cargar el kg allí
      if (tabRef.current === "delivery" && mostradorSelectedRef.current) {
        const sel = mostradorSelectedRef.current;
        const pedido = (deliveryPedidosRef.current || []).find(
          (p) => p.origen === sel.origen && String(p.id) === String(sel.id)
        );
        if (!pedido) {
          toast.error("El pedido seleccionado ya no está disponible");
          return;
        }
        const items = pedido.items || [];
        const getItemCodes = (it) => {
          const arr = [];
          for (const k of ["codigo", "sku", "code", "product_code"]) {
            if (it && it[k] != null && String(it[k]).trim() !== "") {
              arr.push(String(it[k]).trim());
            }
          }
          return arr;
        };
        const matchesCodigo = (it) => {
          const codes = getItemCodes(it);
          if (codes.length === 0) return false;
          return codes.some((cp) =>
            cp === codigoLookup ||
            cp === codigoStr ||
            cp.padStart(codigoLen, "0") === codigoStr ||
            String(parseInt(cp, 10)) === codigoLookup
          );
        };
        const candidatos = items
          .map((it, idx) => ({ it, idx }))
          .filter(({ it }) => matchesCodigo(it));
        if (candidatos.length === 0) {
          const codigosDisponibles = items.map(getItemCodes).flat().filter(Boolean).join(", ") || "sin códigos";
          console.warn("[balanza] no match", { escaneado: codigoLookup, codigosDisponibles, items });
          toast.warning(`Producto "${codigoLookup}" no está en este pedido — ignorado (códigos del pedido: ${codigosDisponibles})`);
          return;
        }
        const disponible = candidatos.find(({ it }) => (it.mostrador_estado || "pendiente") !== "terminado")
          || candidatos[0];
        marcarItemMostrador({
          origen: pedido.origen,
          pedido_id: pedido.id,
          item_idx: disponible.it.item_idx != null ? disponible.it.item_idx : disponible.idx,
          kg_reales: kg,
        }).then(() => {
          const nombre = disponible.it.descripcion || disponible.it.name || disponible.it.producto || "Producto";
          toast.success(`${nombre} · ${kg.toFixed(3)} kg`);
        }).catch(() => { /* toast en marcarItemMostrador */ });
        return;
      }

      // 1b) Fallback: agregar al carrito del POS
      const cand = codigoLookup || "0";
      const p = productos.find((x) => {
        const cp = String(x.codigo || "").trim();
        return cp === cand || cp === codigoStr || cp.padStart(codigoLen, "0") === codigoStr;
      });
      if (!p) {
        toast.error(`Etiqueta balanza: producto "${cand}" no encontrado`);
        return;
      }
      addFromProd(p, kg, 1);
      toast.success(`+ ${p.nombre} · ${kg.toFixed(3)} kg`);
      return;
    }

    // 2) Búsqueda directa por código de producto
    const p = productos.find(
      (x) => String(x.codigo || "").toLowerCase() === c.toLowerCase()
    );
    if (!p) {
      toast.error(`Código "${c}" no encontrado`);
      return;
    }
    // Si es producto por unidad → sumamos 1. Si es por kilo → abrimos keypad.
    if ((p.unidad || "kg").toLowerCase() === "kg") {
      openKeypad(p);
    } else {
      addFromProd(p, 0, 1);
      toast.success(`+ ${p.nombre}`);
    }
  };

  // ---------- Venta ----------
  const buildFacturaPayload = (tipo_comprobante, pagos) => {
    const ivaFactor = 1.105; // 10.5% carne default; el backend recalcula por línea con iva_pct
    // Items del carrito (netos)
    let ivaTotal = 0;
    const payloadItems = items.map((it) => {
      const qty = (it.unidad || "kg").toLowerCase() === "kg" ? parseFloat(it.kilos || 0) : parseFloat(it.cantidad || 1);
      const factor = 1 + ((it.iva_pct || 0) / 100);
      const puCiva = parseFloat(it.precio_unitario || 0);
      const puNeto = factor > 0 ? puCiva / factor : puCiva;
      const subtotalLinea = puNeto * qty;
      const ivaLinea = (puCiva - puNeto) * qty;
      ivaTotal += ivaLinea;
      return {
        producto_id: it.producto_id,
        descripcion: it.descripcion,
        codigo: it.codigo || "",
        tipo_producto: null,
        kilos: (it.unidad || "kg").toLowerCase() === "kg" ? qty : 0,
        cantidad: (it.unidad || "kg").toLowerCase() === "kg" ? 1 : qty,
        precio_unitario: puNeto,
        iva_pct: parseFloat(it.iva_pct || 0),
        subtotal: subtotalLinea,
      };
    });
    // Líneas virtuales de promociones (descuento_promo con importe negativo en neto)
    for (const promo of promosEfectivas) {
      const impCIva = -Math.abs(promo.importe_descuento_total || 0);
      const impNeto = impCIva / ivaFactor;
      ivaTotal += (impCIva - impNeto); // ambos negativos → resta IVA proporcional
      payloadItems.push({
        descripcion: `Descuento · ${promo.promocion_nombre}`,
        codigo: null,
        tipo_producto: "descuento_promo",
        kilos: 0,
        cantidad: 1,
        precio_unitario: impNeto,
        subtotal: impNeto,
        promocion_id: promo.promocion_id,
      });
    }
    // Descuento manual del 5% (cortesía) — línea virtual sobre el total bruto post-promos
    if (totales.descuento_5 > 0) {
      const impCIva = -Math.abs(totales.descuento_5);
      const impNeto = impCIva / ivaFactor;
      ivaTotal += (impCIva - impNeto);
      payloadItems.push({
        descripcion: "Descuento 5% (cortesía)",
        codigo: null,
        tipo_producto: "descuento_manual",
        kilos: 0,
        cantidad: 1,
        precio_unitario: impNeto,
        subtotal: impNeto,
      });
    }
    return {
      tipo: "venta",
      tipo_comprobante,
      fecha: new Date().toISOString().substring(0, 10),
      contraparte_id: cliente?.id || null,
      contraparte_nombre: cliente?.nombre || "Consumidor Final",
      lista_precios_id: listaId || null,
      unidad_negocio: "Mostrador",
      origen: "pos",  // Marca de origen para separar del listado general de Facturas
      items: payloadItems,
      iva: Math.max(0, Math.round(ivaTotal * 100) / 100),
      iva_pct: items[0]?.iva_pct != null ? items[0].iva_pct : 10.5,
      total_kilos: items.reduce((s, it) => s + ((it.unidad || "kg").toLowerCase() === "kg" ? parseFloat(it.kilos || 0) : 0), 0),
      observaciones: observaciones || null,
    };
  };

  const submitVenta = async (mode, pagos, extra = {}, bypassListaCheck = false) => {
    // mode: "cobrar" (Remito X, no fiscal) o "facturar_cobrar" (Factura B con AFIP)
    if (!sesion?.active) return toast.error("La caja está cerrada. Abrila desde 'Caja Diaria → Datos'.");
    if (items.length === 0) return toast.error("Carrito vacío");
    if (!cliente) return toast.error("Seleccioná un cliente");
    const totalPagos = pagos.reduce((s, p) => s + parseFloat(p.monto || 0), 0);
    if (Math.abs(totalPagos - totales.total) > 0.01) {
      return toast.error(`Los pagos ($${totalPagos.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) no coinciden con el total ($${totales.total.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`);
    }

    // Chequeo previo: precio no corresponde a la lista asignada al cliente.
    // Se resuelve la lista EFECTIVA (default del usuario pisa la del cliente).
    // El operador puede "Modificar venta" o "Facturar igual" (bypass=true).
    const listaEfectivaId = getEffectiveListaId(cliente, user);
    if (!bypassListaCheck && listaEfectivaId) {
      // Items del POS traen precio NET (backend suma IVA por ítem).
      const mismatches = checkListaIncorrecta({
        items,
        cliente: { ...cliente, lista_precios_id: listaEfectivaId },
        productos,
        listasPrecios: listas,
        pricesNet: true,
      });
      if (mismatches.length > 0) {
        setListaAlerta({ mismatches, mode, pagos, extra });
        return; // pausamos hasta que el usuario decida
      }
    }

    setProcessing(true);
    try {
      let tipo_cbte;
      let auto_afip = false;
      if (mode === "cobrar") {
        tipo_cbte = "REMITO X";
      } else {
        // Facturar y cobrar: Fac A si el cliente es RI con CUIT, sino Fac B
        const cond = (cliente.condicion_iva || "").toLowerCase();
        tipo_cbte = cond.includes("responsable") ? "FACTURA A" : "FACTURA B";
        auto_afip = true;
      }

      const payload = buildFacturaPayload(tipo_cbte, pagos);
      const { data: factura } = await api.post(`/facturas?auto_afip=${auto_afip}`, payload);

      // Registrar consumo de promociones (para respetar max_kg, max_unidades, etc.)
      for (const promo of promosEfectivas) {
        try {
          await api.post(`/promociones/${promo.promocion_id}/consumo`, {
            factura_id: factura.id,
            cliente_id: cliente?.id || null,
            kilos: promo.kilos_aplicados || 0,
            unidades: promo.unidades_aplicadas || 0,
            importe_descuento: Math.abs(promo.importe_descuento_total || 0),
          });
        } catch (err) {
          console.warn("Consumo promo falló:", err);
        }
      }

      // Crear los movimientos de caja + cobro para cada pago.
      // Si el operador eligió "Cuenta Corriente" (caja_id = "cta_cte"), no se
      // crean cobros: la factura queda pendiente sumando al saldo del cliente.
      const cobros = [];
      const cobrosFallidos = [];
      const esCtaCte = pagos.length === 1 && pagos[0].caja_id === "cta_cte";
      if (!esCtaCte) {
        for (const p of pagos) {
          if (!p.caja_id || !p.monto) continue;
          try {
            const { data: cobro } = await api.post("/cobros-pagos", {
              tipo: "cobro",
              contraparte_id: cliente.id,
              caja_id: p.caja_id,
              fecha: new Date().toISOString().substring(0, 10),
              monto: parseFloat(p.monto),
              concepto: `POS · ${tipo_cbte} ${factura.numero || ""}`,
              facturas_aplicadas: [factura.id],
            });
            cobros.push(cobro);
          } catch (err) {
            console.warn("Error registrando pago:", err);
            cobrosFallidos.push(err?.response?.data?.detail || "Error al registrar el pago en la caja");
          }
        }
      }

      setLastSale({ factura, cobros, mode });
      if (cobrosFallidos.length > 0) {
        toast.error(
          `La venta ${factura.numero || ""} se emitió pero el pago NO se registró en caja y quedó en cuenta corriente del cliente: ${cobrosFallidos[0]}`,
          { duration: 12000 }
        );
      }
      const detalle = esCtaCte
        ? `Venta a cuenta corriente · Total ${fmtARS(totales.total)}`
        : `Venta ${mode === "cobrar" ? "registrada" : "facturada"} · Total ${fmtARS(totales.total)}`;
      toast.success(detalle);

      // Vincular pedido de Delivery (retiro en local) a la venta recién confirmada
      // Esto saca al pedido del listado Delivery. Si el operador cancela antes de
      // llegar acá, el pedido permanece visible.
      if (pedidoDeliveryOrigen) {
        try {
          await api.post("/pos/delivery/marcar-facturado", {
            origen: pedidoDeliveryOrigen.origen,
            pedido_id: pedidoDeliveryOrigen.id,
            factura_id: factura.id,
            factura_numero: factura.numero || "",
          });
          loadDelivery({ silent: true });
        } catch (err) {
          console.warn("Vincular pedido delivery falló:", err);
        }
        setPedidoDeliveryOrigen(null);
      }

      // Impresión del Ticket por comandera (fiscal para Factura A/B/C, no fiscal para Remito X)
      const tipoUp = (factura.tipo_comprobante || "").toUpperCase();
      const esFactura = tipoUp === "FACTURA A" || tipoUp === "FACTURA B" || tipoUp === "FACTURA C";
      const puedeImprimir = (esFactura && factura.cae) || (!esFactura && (factura.items?.length || 0) > 0);
      if (puedeImprimir) {
        const etiqueta = esFactura ? "ticket fiscal" : "ticket";
        const wantsPrint = window.confirm(
          `¿Imprimir ${etiqueta} ${factura.tipo_comprobante} ${factura.numero || ""} por la comandera?\n\nPodés reimprimirlo después desde "Caja Diaria → Ingresos por venta".`
        );
        if (wantsPrint) {
          try {
            await imprimirTicketFiscal(factura, {
              metodo_pago: extra.metodo_pago || "",
              vuelto: extra.vuelto || 0,
            });
          } catch (err) {
            console.error("Error imprimiendo ticket:", err);
            toast.error("Falló la impresión. Podés reimprimirlo desde 'Caja Diaria → Ingresos por venta'.");
          }
        }
      }

      // Reset carrito
      setItems([]);
      setObservaciones("");
      setActiveSaleId(null);
      setPromosDescartadas(new Set());
      setPromosAplicables([]);
      setPayDialog(null);
      setPagoEfectivoOn(false);
      setDescuento5On(false);
      // Volver a Consumidor Final para no dejar el cliente anterior seleccionado
      const cf = clientes.find((c) =>
        (c.nombre || "").toLowerCase().trim() === "consumidor final" ||
        c.tipo_documento === "CF"
      );
      if (cf) {
        setCliente(cf);
        // Volver a la lista POS default del setting (no la del cliente).
        if (posListaDefaultId) setListaId(posListaDefaultId);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al procesar la venta");
    } finally {
      setProcessing(false);
    }
  };

  const downloadPdf = async (facturaId) => {
    try {
      const res = await api.get(`/afip/pdf/${facturaId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `factura-${facturaId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("PDF sólo disponible para facturas con CAE. Descargá desde /ventas.");
    }
  };

  // ---------- Render ----------
  return (
    <div className="h-screen flex flex-col bg-[#F5F5F5] overflow-hidden">
      {/* Barra superior estilo LA BIFERIA (rojo bordó + crema) */}
      <div className="bg-[#6B1F24] text-white flex items-center h-10 px-2 gap-1 text-[13px] flex-shrink-0 shadow-md">
        <button
          onClick={doExit}
          className="hover:bg-[#8F191C] px-3 h-full flex items-center gap-1.5 transition-colors"
          data-testid={backTarget ? "pos-back" : "pos-logout"}
          title={backTarget ? "Volver al ERP" : "Cerrar sesión"}
        >
          {backTarget
            ? <><ArrowLeft size={14} /> Salir</>
            : <><SignOut size={14} weight="bold" /> Cerrar sesión</>}
        </button>
        <button
          onClick={() => setTopView("ventas")}
          data-testid="top-tab-ventas"
          className={
            topView === "ventas"
              ? "bg-[#F9F6F6] text-[#6B1F24] px-4 h-full flex items-center gap-2 font-heading font-semibold shadow-inner"
              : "text-white/80 hover:bg-[#8F191C] px-4 h-full flex items-center gap-2 font-heading"
          }
        >
          <CashRegister size={16} weight="bold" /> Punto de Venta
        </button>
        <button
          onClick={() => setTopView("caja")}
          data-testid="top-tab-caja"
          className={
            topView === "caja"
              ? "bg-[#F9F6F6] text-[#6B1F24] px-4 h-full flex items-center gap-2 font-heading font-semibold shadow-inner"
              : "text-white/80 hover:bg-[#8F191C] px-4 h-full flex items-center gap-2 font-heading"
          }
        >
          <ClockCounterClockwise size={14} weight="bold" /> Caja Diaria
        </button>

        {/* Ventas como pestañas — orden fijo por creación (tabOrder) */}
        <div className="flex items-end gap-0 ml-3 h-full" data-testid="pos-tabs">
          {tabOrder.map((tid) => {
            const isActive = tid === activeSaleId;
            const snapshot = isActive
              ? { id: tid, cliente, items }
              : parked.find((s) => s.id === tid);
            if (!snapshot) return null;  // orphan (fue eliminada)
            const nombre = snapshot.cliente?.nombre?.split(" ")[0] || "S/C";
            const nItems = snapshot.items?.length || 0;
            return (
              <div
                key={tid}
                data-testid={isActive ? "tab-venta-activa" : `parked-${tid}`}
                className={
                  isActive
                    ? "px-3 h-[calc(100%-4px)] flex items-center gap-1.5 text-[11px] bg-[#F9F6F6] text-[#6B1F24] rounded-t-md border-t border-x border-[#F0D1B6] shadow-sm relative -mb-px"
                    : "group flex items-center h-[calc(100%-4px)] bg-[#8F191C]/40 hover:bg-[#F0D1B6] hover:text-[#6B1F24] rounded-t-md border-t border-x border-[#F0D1B6]/40 hover:border-[#F0D1B6] text-white transition-colors cursor-pointer -mb-px"
                }
                title={`Cliente: ${snapshot.cliente?.nombre || "—"} · ${nItems} ítems`}
              >
                {isActive ? (
                  <>
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="font-heading font-semibold">{nombre}</span>
                    <span className="font-mono text-[10px] text-[#921A0E]">· {nItems}</span>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => resumeParked(snapshot)}
                      className="px-3 h-full flex items-center gap-1.5 text-[11px]"
                    >
                      <span className="font-heading font-semibold">{nombre}</span>
                      <span className="font-mono text-[10px] opacity-70">· {nItems}</span>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); dropParked(tid); }}
                      className="pr-2 h-full opacity-40 hover:opacity-100"
                      title="Descartar venta"
                    >
                      <X size={11} />
                    </button>
                  </>
                )}
              </div>
            );
          })}

          {/* Botón + nueva pestaña (siempre visible) */}
          <button
            onClick={newEmptyTab}
            data-testid="btn-nueva-tab"
            className="px-2 h-[calc(100%-4px)] flex items-center rounded-t-md hover:bg-white/15 text-white/70 hover:text-white -mb-px transition-colors"
            title="Nueva venta en pestaña"
          >
            <Plus size={13} weight="bold" />
          </button>
        </div>

        <div className="ml-auto flex items-center gap-3 text-[12px] pr-3 font-medium">
          <span className="text-[#F0D1B6]">🥩 LA BIFERIA · Sucursal Mostrador</span>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {topView === "caja" ? (
          <CajaDiariaView
            sesion={sesion}
            sesionLoading={sesionLoading}
            onReload={loadSesion}
            onCierreClick={() => setCierreOpen(true)}
          />
        ) : tab === "delivery" ? (
          <DeliveryMostradorView
            pedidos={deliveryPedidos}
            loading={deliveryLoading}
            onReload={loadDelivery}
            onMarcarItem={marcarItemMostrador}
            onVolver={() => { setMostradorSelected(null); setTab("mostrador"); }}
            onImprimir={imprimirTicketConMarca}
            selectedPedido={mostradorSelected}
            onSelectPedido={setMostradorSelected}
            onCargarAlPOS={cargarPedidoAlPOS}
            onMarcarEntregado={marcarEntregado}
          />
        ) : (
        <>
        {/* ============================== PANEL IZQUIERDO ============================== */}
        <div className="w-[42%] bg-[#F9F6F6] border-r-2 border-[#6B1F24]/20 flex flex-col">
          {/* Subtabs Inicio / Mostrador / Delivery */}
          <div className="border-b border-[#6B1F24]/20 flex items-center h-9 text-[12px] px-2 gap-3 bg-white">
            <button className="px-2 h-full text-neutral-500 font-medium">Inicio</button>
            <button
              onClick={() => setTab("mostrador")}
              data-testid="tab-mostrador"
              className={`px-2 h-full font-heading ${tab === "mostrador" ? "text-[#921A0E] border-b-2 border-[#921A0E] font-semibold" : "text-neutral-500"}`}
            >
              Mostrador
            </button>
            <button
              onClick={() => { setTab("delivery"); loadDelivery(); }}
              data-testid="tab-delivery"
              className={`relative px-2 pr-5 h-full font-heading ${tab === "delivery" ? "text-[#921A0E] border-b-2 border-[#921A0E] font-semibold" : "text-neutral-500 hover:text-[#921A0E]"}`}
            >
              Delivery
              {deliveryPedidos.length > 0 && (
                <span
                  data-testid="tab-delivery-badge"
                  className="absolute top-1/2 -translate-y-1/2 right-0 min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#921A0E] text-white text-[11px] font-bold flex items-center justify-center leading-none shadow-md ring-2 ring-white animate-pulse"
                >
                  {deliveryPedidos.length > 99 ? "99+" : deliveryPedidos.length}
                </span>
              )}
            </button>
          </div>

          {/* Cabecera cliente / lista / obs */}
          <div className="px-3 py-2.5 border-b border-[#6B1F24]/10 space-y-2 text-[12px] bg-white">
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <button
                  onClick={clearCart}
                  className="w-8 h-8 rounded-sm border border-[#6B1F24]/20 hover:bg-[#F0D1B6]/30 flex items-center justify-center transition-colors"
                  title="Vaciar carrito"
                  data-testid="pos-clear-cart"
                >
                  <Trash size={14} className="text-[#8F191C]" />
                </button>
                <button
                  onClick={() => setQuickClienteOpen(true)}
                  className="w-8 h-8 rounded-sm border border-[#6B1F24]/20 hover:bg-[#F0D1B6]/30 flex items-center justify-center transition-colors"
                  title="Cliente rápido"
                  data-testid="pos-cliente-rapido"
                >
                  <UserPlus size={14} className="text-[#6B1F24]" />
                </button>
              </div>
              <div className="flex-1 grid grid-cols-[70px_1fr] items-center gap-2">
                <Label className="text-[12px] text-neutral-700 font-medium">Cliente</Label>
                <button
                  onClick={() => setClienteDialogOpen(true)}
                  data-testid="pos-cliente-btn"
                  className="border border-[#6B1F24]/20 rounded-sm px-2 py-1 text-left text-[12px] hover:bg-[#F0D1B6]/20 flex items-center justify-between transition-colors bg-white"
                >
                  <span className="truncate font-medium">{cliente?.nombre || "Consumidor Final"}</span>
                  <User size={14} className="text-[#6B1F24]" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-[70px_1fr] items-center gap-2">
              <Label className="text-[12px] text-neutral-700 font-medium">Lista precios</Label>
              <Select value={listaId || ""} onValueChange={setListaId}>
                <SelectTrigger className="h-7 text-[12px] border-[#6B1F24]/20" data-testid="pos-lista-select">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {listas.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <details>
              <summary className="cursor-pointer text-[12px] text-neutral-700 select-none font-medium">Observaciones</summary>
              <Input
                data-testid="pos-observaciones"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                className="mt-1 h-7 text-[12px] border-[#6B1F24]/20"
                placeholder="Observación del comprobante"
              />
            </details>
          </div>

          {/* Tabla de ítems */}
          <div className="flex-1 overflow-auto">
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-neutral-100 sticky top-0 z-10">
                  <tr className="text-[10px] uppercase text-neutral-500 font-mono">
                    <th className="text-left px-2 py-1.5 w-8"></th>
                    <th className="text-left px-2 py-1.5 w-16">Código</th>
                    <th className="text-left px-2 py-1.5">Descripción</th>
                    <th className="text-right px-2 py-1.5 w-24">Cantidad</th>
                    <th className="text-right px-2 py-1.5 w-24">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const qty = (it.unidad || "kg").toLowerCase() === "kg" ? it.kilos : it.cantidad;
                    const importe = qty * (it.precio_unitario || 0);
                    return (
                      <tr
                        key={i}
                        className="border-b border-[#6B1F24]/10 hover:bg-[#F0D1B6]/20 group cursor-pointer select-none"
                        data-testid={`pos-cart-row-${i}`}
                        onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); openKeypadForRow(i); }}
                        title="Doble-click para modificar los kilos"
                      >
                        <td className="px-1 py-1.5 text-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); removeItem(i); }}
                            className="opacity-0 group-hover:opacity-100 transition text-red-500"
                            title="Quitar"
                          >
                            <X size={12} />
                          </button>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[#6B1F24]">{it.codigo}</td>
                        <td className="px-2 py-1.5 truncate">{it.descripcion}</td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          <span className="font-bold">{Number(qty).toLocaleString("es-AR", { maximumFractionDigits: 3 })}</span>
                          <span className="text-[10px] text-neutral-500 ml-0.5">{it.unidad || "kg"}</span>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmtARS(importe)}</td>
                      </tr>
                    );
                  })}
                  {/* Líneas de promociones aplicadas (visualización) */}
                  {promosEfectivas.map((p) => (
                    <tr key={`promo-${p.promocion_id}`} className="bg-emerald-50 text-emerald-800" data-testid={`pos-promo-${p.promocion_id}`}>
                      <td className="px-1 py-1"></td>
                      <td className="px-2 py-1 text-[10px] uppercase">PROMO</td>
                      <td className="px-2 py-1 text-[11px] italic">{p.promocion_nombre}</td>
                      <td className="px-2 py-1"></td>
                      <td className="px-2 py-1 text-right font-mono font-bold">- {fmtARS(Math.abs(p.importe_descuento_total || 0))}</td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-16 text-neutral-400 text-[12px]">
                        Sin ítems. Tocá un producto o escaneá un código de barras.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer con totales + botones */}
          <div className="border-t-2 border-[#6B1F24]/20 bg-white flex-shrink-0">
            {/* Banner de promociones */}
            {promosAplicables.length > 0 && (
              <div className="px-3 py-1.5 bg-emerald-50 border-b border-emerald-200 text-[11px]" data-testid="promo-banner">
                <div className="font-mono uppercase text-[9px] text-emerald-800 mb-0.5">
                  Promociones · {promosEfectivas.length}/{promosAplicables.length} aplicadas
                </div>
                {promosAplicables.map((p) => {
                  const off = promosDescartadas.has(p.promocion_id);
                  return (
                    <button
                      key={p.promocion_id}
                      onClick={() => togglePromo(p.promocion_id)}
                      data-testid={`promo-toggle-${p.promocion_id}`}
                      className={`inline-flex items-center gap-1 mr-2 mb-0.5 px-1.5 py-0.5 rounded ${off ? "line-through text-neutral-400 bg-neutral-100" : "bg-emerald-100 text-emerald-800"}`}
                      title={off ? "Reactivar promo" : "Descartar promo"}
                    >
                      <span className="text-[10px]">{off ? "○" : "✓"}</span>
                      <span className="font-medium">{p.promocion_nombre}</span>
                      <span className="font-mono">-{fmtARS(Math.abs(p.importe_descuento_total || 0))}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="px-3 py-2.5 flex items-center justify-between text-[12px] border-b border-[#6B1F24]/10 bg-[#F0D1B6]/20">
              <div className="text-neutral-600 font-mono flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPagoEfectivoOn((v) => !v)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded font-heading font-bold uppercase text-[13px] transition-all ${
                    pagoEfectivoOn
                      ? "bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-300"
                      : "bg-white text-neutral-500 border border-neutral-300 hover:bg-neutral-50"
                  }`}
                  title={pagoEfectivoOn
                    ? "Pago en EFECTIVO activado (aplica descuento). Click para desactivar."
                    : "Activar pago en EFECTIVO para aplicar descuentos por efectivo"}
                  data-testid="pos-toggle-efectivo"
                >
                  <span>{pagoEfectivoOn ? "✓" : "$"}</span>
                  <span>EFECTIVO</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDescuento5On((v) => !v)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded font-heading font-bold uppercase text-[13px] transition-all ${
                    descuento5On
                      ? "bg-amber-600 text-white shadow-sm ring-2 ring-amber-300"
                      : "bg-white text-neutral-500 border border-neutral-300 hover:bg-neutral-50"
                  }`}
                  title={descuento5On
                    ? "Descuento 5% aplicado (cortesía). Click para desactivar."
                    : "Aplicar un 5% de descuento por atención/cortesía"}
                  data-testid="pos-toggle-desc5"
                >
                  <span>{descuento5On ? "✓" : "%"}</span>
                  <span>DESC 5%</span>
                </button>
                <span>
                  Filas: <strong className="text-[#6B1F24]">{totales.rows}</strong>{" · "}
                  Items: <strong className="text-[#6B1F24]">{totales.items_count.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </span>
                {totales.descuento_promo > 0 && (
                  <span className="ml-2 text-emerald-700">· Desc: -{fmtARS(totales.descuento_promo)}</span>
                )}
                {totales.descuento_5 > 0 && (
                  <span className="ml-2 text-amber-700">· 5%: -{fmtARS(totales.descuento_5)}</span>
                )}
              </div>
              <div className="text-[22px] font-bold font-mono text-[#6B1F24]" data-testid="pos-total">
                {fmtARS(totales.total)}
              </div>
            </div>
            <div className="p-2 grid grid-cols-2 gap-2">
              <Button
                data-testid="btn-cobrar"
                disabled={items.length === 0 || processing || !sesion?.active}
                onClick={() => setPayDialog("cobrar")}
                className="h-14 bg-[#6B1F24] hover:bg-[#8F191C] text-white font-heading uppercase tracking-widest text-[13px] shadow-md transition-all"
              >
                {sesion?.active ? "Cobrar" : "🔒 Caja cerrada"}
              </Button>
              <Button
                data-testid="btn-facturar-cobrar"
                disabled={items.length === 0 || processing || !sesion?.active}
                onClick={() => setPayDialog("facturar_cobrar")}
                className="h-14 bg-[#4DC247] hover:bg-[#3fa63a] text-white font-heading uppercase tracking-widest text-[13px] shadow-md transition-all"
              >
                {sesion?.active ? "Facturar y Cobrar" : "🔒 Caja cerrada"}
              </Button>
            </div>
          </div>
        </div>

        {/* ============================== PANEL DERECHO ============================== */}
        <div className="flex-1 flex flex-col bg-[#F9F6F6] overflow-hidden">
          <div className="border-b border-[#6B1F24]/20 px-4 py-2.5 flex items-center gap-3 bg-white">
            <Label className="text-[13px] text-[#6B1F24] font-heading font-semibold">Artículo</Label>
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#6B1F24]/60" />
              <Input
                data-testid="pos-article-search"
                ref={articleSearchRef}
                value={articleSearch}
                onChange={(e) => setArticleSearch(e.target.value)}
                placeholder="Buscar por nombre o código..."
                className="pl-7 h-9 text-[13px] border-[#6B1F24]/20 focus:border-[#921A0E]"
              />
            </div>
            <Button
              data-testid="btn-scan-barcode"
              variant="outline"
              size="sm"
              onClick={() => setScannerOpen(true)}
              title="Escanear código de barras"
              className="h-9 gap-1 border-[#6B1F24]/30 hover:bg-[#F0D1B6]/30 text-[#6B1F24]"
            >
              <Barcode size={16} weight="bold" />
            </Button>
          </div>

          {/* Breadcrumb */}
          <div className="px-4 py-2 border-b border-[#6B1F24]/10 text-[12px] flex items-center gap-2 bg-white">
            {selectedRubroId !== DEFAULT_RUBRO_ID && (
              <button
                onClick={() => setSelectedRubroId(DEFAULT_RUBRO_ID)}
                className="text-[#921A0E] font-heading font-bold uppercase text-[11px] hover:text-[#6B1F24] transition-colors"
                data-testid="pos-breadcrumb-inicio"
              >
                INICIO
              </button>
            )}
            {selectedRubroId !== DEFAULT_RUBRO_ID && (
              <>
                <span className="text-neutral-400">/</span>
                <span className="text-neutral-700 uppercase text-[11px] font-mono font-semibold">
                  {rubros.find((r) => r.id === selectedRubroId)?.nombre || "—"}
                </span>
              </>
            )}
            {selectedRubroId === DEFAULT_RUBRO_ID && (
              <span className="text-[#921A0E] font-heading font-bold uppercase text-[11px]">INICIO</span>
            )}
          </div>

          {/* Grid: rubros (si estamos en INICIO) o productos (si estamos dentro de un rubro / buscando) */}
          <div className="flex-1 overflow-auto p-4">
            {selectedRubroId === DEFAULT_RUBRO_ID && !articleSearch.trim() ? (
              <>
                <RubroGrid rubros={rubros} onSelect={(rid) => setSelectedRubroId(rid)} />
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setShowPromosDialog(true)}
                    data-testid="pos-btn-promociones"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded bg-gradient-to-r from-[#921A0E] to-[#6B1F24] text-white font-heading uppercase tracking-widest text-[13px] font-bold shadow hover:from-[#6B1F24] hover:to-[#4d0f0d] active:scale-95 transition-all"
                  >
                    <span className="text-lg">%</span> PROMOCIONES
                  </button>
                </div>
              </>
            ) : (
              <ProductGrid
                productos={filtered}
                priceOf={priceOf}
                onSelect={(p) => openKeypad(p)}
                onDoubleSelect={(p) => swapLastRowProduct(p)}
              />
            )}
          </div>
        </div>
        </>
        )}
      </div>

      {/* Keypad numérico */}
      {keypadProd && (
        <NumericKeypad
          producto={keypadProd.producto}
          precio={priceOf(keypadProd.producto)}
          initialValue={keypadProd.initialValue}
          productos={productos}
          priceOf={priceOf}
          allowChangeProduct={keypadProd.editRowIdx != null}
          onChangeProduct={(newProd) => {
            // Reemplaza el producto de la fila que estamos editando manteniendo la cantidad ingresada
            setKeypadProd((k) => k ? { ...k, producto: newProd } : k);
            if (keypadProd.editRowIdx != null) {
              setItems((prev) => prev.map((it, idx) => {
                if (idx !== keypadProd.editRowIdx) return it;
                const precio = priceOf(newProd);
                return {
                  ...it,
                  producto_id: newProd.id,
                  codigo: newProd.codigo,
                  descripcion: newProd.nombre,
                  unidad: newProd.unidad || "kg",
                  precio_unitario: precio,
                  iva_pct: newProd.iva_pct != null ? newProd.iva_pct : 10.5,
                };
              }));
            }
          }}
          onCancel={() => setKeypadProd(null)}
          onConfirm={(valor) => {
            if (keypadProd.editRowIdx != null) {
              // Modificar cantidad de línea existente
              setItems((prev) => prev.map((it, idx) => {
                if (idx !== keypadProd.editRowIdx) return it;
                const isKg = (it.unidad || "kg").toLowerCase() === "kg";
                return isKg ? { ...it, kilos: valor } : { ...it, cantidad: valor };
              }));
              toast.success(`Cantidad actualizada: ${valor}`);
            } else if ((keypadProd.producto.unidad || "kg").toLowerCase() === "kg") {
              addFromProd(keypadProd.producto, valor, 1);
            } else {
              addFromProd(keypadProd.producto, 0, valor);
            }
            setKeypadProd(null);
            articleSearchRef.current?.focus();
          }}
        />
      )}

      {/* Scanner manual */}
      <Dialog open={scannerOpen} onOpenChange={(v) => { setScannerOpen(v); if (!v) setScanValue(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Barcode size={18} /> Escanear código de barras</DialogTitle>
            <DialogDescription>Enfocá el input y dispará el lector. Con Enter se procesa.</DialogDescription>
          </DialogHeader>
          <Input
            data-testid="scan-input"
            autoFocus
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleScan(scanValue);
                setScanValue("");
                setScannerOpen(false);
              }
            }}
            placeholder="Código..."
            className="font-mono h-11 text-lg"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setScannerOpen(false)}>Cerrar</Button>
            <Button onClick={() => { handleScan(scanValue); setScanValue(""); setScannerOpen(false); }} className="bg-[#6B1F24] hover:bg-[#8F191C]">
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cliente selector */}
      <Dialog open={clienteDialogOpen} onOpenChange={setClienteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Seleccionar cliente</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            data-testid="cliente-search-input"
            placeholder="Buscar por nombre o CUIT..."
            value={clienteSearch}
            onChange={(e) => setClienteSearch(e.target.value)}
          />
          <div className="max-h-96 overflow-y-auto border border-neutral-200 rounded">
            {clientes
              .filter((c) => {
                const q = clienteSearch.toLowerCase();
                return !q || (c.nombre || "").toLowerCase().includes(q) || (c.cuit || "").includes(q);
              })
              .slice(0, 30)
              .map((c) => (
                <button
                  key={c.id}
                  data-testid={`cliente-option-${c.id}`}
                  onClick={() => {
                    setCliente(c);
                    // POS: la lista NUNCA cambia según el cliente (regla nueva).
                    // Sólo se cambia manualmente desde el dropdown de listas.
                    setClienteDialogOpen(false);
                    setClienteSearch("");
                  }}
                  className="block w-full text-left px-3 py-2 border-b border-neutral-100 hover:bg-neutral-50 text-[13px]"
                >
                  <div className="font-medium">{c.nombre}</div>
                  <div className="text-[11px] text-neutral-500 font-mono">
                    {c.cuit || c.dni || "s/documento"} · {c.condicion_iva || "—"}
                  </div>
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo de pago */}
      {payDialog && (
        <PaymentDialog
          mode={payDialog}
          total={totales.total}
          cajas={cajas}
          mediosPago={mediosPago}
          cliente={cliente}
          processing={processing}
          onCancel={() => setPayDialog(null)}
          onConfirm={(pagos, extra) => submitVenta(payDialog, pagos, extra)}
        />
      )}

      {/* Alerta: precio no corresponde a la lista del cliente */}
      <AlertaListaPrecioModal
        open={!!listaAlerta}
        onClose={() => setListaAlerta(null)}
        onConfirm={() => {
          const ctx = listaAlerta;
          setListaAlerta(null);
          if (ctx) submitVenta(ctx.mode, ctx.pagos, ctx.extra, true);
        }}
        cliente={cliente}
        listaClienteNombre={(listas.find((l) => l.id === (user?.lista_precios_default_id || cliente?.lista_precios_id)) || {}).nombre}
        mismatches={listaAlerta?.mismatches || []}
        actionLabel="Facturar igual"
      />

      <AlertaExcesoKgTNModal
        data={excesoKgAlerta}
        onClose={() => setExcesoKgAlerta(null)}
      />


      {/* Post-venta: descargar PDF */}
      <Dialog open={!!lastSale} onOpenChange={(v) => !v && setLastSale(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Receipt size={20} /> Venta registrada
            </DialogTitle>
          </DialogHeader>
          {lastSale && (
            <div className="space-y-3 text-[13px]">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                <div className="font-mono text-[12px]">
                  <div><strong>Nº:</strong> {lastSale.factura?.numero || "—"}</div>
                  <div><strong>Tipo:</strong> {lastSale.factura?.tipo_comprobante}</div>
                  <div><strong>Total:</strong> {fmtARS(lastSale.factura?.total || 0)}</div>
                  {lastSale.factura?.cae && <div><strong>CAE:</strong> {lastSale.factura.cae}</div>}
                </div>
              </div>
              <div className="text-[11px] text-neutral-500">
                Impresión térmica 80mm pendiente (se configura en la PC del local). Por ahora podés descargar el PDF.
              </div>
              <div className="grid grid-cols-2 gap-2">
                {lastSale.factura?.cae && (
                  <Button data-testid="btn-download-pdf" onClick={() => downloadPdf(lastSale.factura.id)} variant="outline" className="gap-2">
                    <Download size={14} /> Descargar PDF
                  </Button>
                )}
                <Button data-testid="btn-nueva-venta" onClick={() => setLastSale(null)} className="bg-[#6B1F24] hover:bg-[#8F191C]">
                  Nueva venta
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cliente rápido */}
      {quickClienteOpen && (
        <QuickClienteDialog
          onCancel={() => setQuickClienteOpen(false)}
          onCreated={(nuevo) => {
            setClientes((prev) => [nuevo, ...prev]);
            setCliente(nuevo);
            // POS: no forzamos la lista del cliente al seleccionarlo — la lista
            // POS default se mantiene salvo cambio manual.
            setQuickClienteOpen(false);
            toast.success(`Cliente ${nuevo.nombre} creado y seleccionado`);
          }}
        />
      )}

      {/* Cierre de Caja X/Z */}
      {cierreOpen && (
        <CierreCajaDialog
          onClose={() => setCierreOpen(false)}
        />
      )}

      {/* Diálogo Promociones (solo lectura) */}
      <PromocionesReadOnlyDialog
        open={showPromosDialog}
        onClose={() => setShowPromosDialog(false)}
        productos={productos}
        clientes={clientes}
        listas={listas}
      />

    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Diálogo de solo lectura para ver promociones vigentes desde el POS
// ═════════════════════════════════════════════════════════════════════
const DIAS_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function PromocionesReadOnlyDialog({ open, onClose, productos = [], clientes = [], listas = [] }) {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/promociones", { params: { activas: true } })
      .then(({ data }) => setPromos(Array.isArray(data) ? data : []))
      .catch(() => setPromos([]))
      .finally(() => setLoading(false));
  }, [open]);

  const nombreProducto = (id) => productos.find((p) => p.id === id)?.nombre || id;
  const nombreCliente = (id) => clientes.find((c) => c.id === id)?.nombre || id;
  const nombreLista = (id) => listas.find((l) => l.id === id)?.nombre || id;

  const fmtDescuento = (d) => {
    if (!d) return "—";
    if (d.tipo === "porcentaje") return `${d.valor}% off`;
    if (d.tipo === "monto_por_unidad") return `${fmtARS(d.valor)} por unidad`;
    if (d.tipo === "precio_fijo") return `Precio fijo ${fmtARS(d.valor)}`;
    return `${d.tipo}: ${d.valor}`;
  };

  const fmtCondicionMedios = (medios) => {
    if (!medios || !medios.length) return null;
    // Mapear IDs conocidos a nombres legibles
    const nombres = medios.map((id) => id === "cta_cte" ? "Cuenta Corriente" : "Efectivo/Tarjeta");
    return nombres.join(", ");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#6B1F24]">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-[#921A0E] to-[#6B1F24] text-white font-bold">%</span>
            Promociones y Descuentos vigentes
          </DialogTitle>
          <DialogDescription>
            Todas las promociones activas del sistema. Solo lectura — para editar ingresá a <strong>Admin → Promociones y Descuentos</strong>.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-12 text-neutral-500">Cargando…</div>
        ) : promos.length === 0 ? (
          <div className="border-2 border-dashed border-neutral-300 rounded p-12 text-center">
            <div className="text-neutral-500">No hay promociones activas en este momento.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {promos.map((p) => {
              const c = p.condiciones || {};
              const d = p.descuento || {};
              return (
                <div key={p.id} className="border-2 border-[#6B1F24]/30 rounded overflow-hidden bg-white" data-testid={`promo-item-${p.id}`}>
                  <div className="bg-gradient-to-r from-[#F0D1B6]/40 to-[#F0D1B6]/10 px-4 py-3 border-b border-[#6B1F24]/20">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="font-heading font-bold uppercase text-[#6B1F24] text-lg tracking-wide">{p.nombre}</div>
                        {p.descripcion && (
                          <div className="text-[13px] text-neutral-600 mt-0.5">{p.descripcion}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase font-mono text-neutral-500">Descuento</div>
                        <div className="font-bold text-emerald-700 text-lg font-mono">{fmtDescuento(d)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]">
                    {p.codigo && (
                      <div><span className="text-neutral-500">Código cupón:</span> <span className="font-mono font-bold">{p.codigo}</span></div>
                    )}
                    {(c.fecha_desde || c.fecha_hasta) && (
                      <div><span className="text-neutral-500">Vigencia:</span> <span className="font-mono">{c.fecha_desde || "—"} → {c.fecha_hasta || "sin vencimiento"}</span></div>
                    )}
                    {c.dias_semana && c.dias_semana.length > 0 && c.dias_semana.length < 7 && (
                      <div><span className="text-neutral-500">Días:</span> <span>{c.dias_semana.map((d) => DIAS_SEMANA[d]).join(", ")}</span></div>
                    )}
                    {c.producto_ids && c.producto_ids.length > 0 && (
                      <div className="col-span-full"><span className="text-neutral-500">Productos:</span> <span>{c.producto_ids.map(nombreProducto).join(", ")}</span></div>
                    )}
                    {c.cliente_ids && c.cliente_ids.length > 0 && (
                      <div className="col-span-full"><span className="text-neutral-500">Clientes:</span> <span>{c.cliente_ids.map(nombreCliente).join(", ")}</span></div>
                    )}
                    {c.lista_precios_ids && c.lista_precios_ids.length > 0 && (
                      <div className="col-span-full"><span className="text-neutral-500">Lista de precios:</span> <span>{c.lista_precios_ids.map(nombreLista).join(", ")}</span></div>
                    )}
                    {c.medios_pago && c.medios_pago.length > 0 && (
                      <div><span className="text-neutral-500">Medios de pago:</span> <span className="font-medium">Solo Efectivo</span></div>
                    )}
                    {c.cantidad_min_kg != null && (
                      <div><span className="text-neutral-500">Mínimo:</span> <span className="font-mono">{c.cantidad_min_kg} kg</span></div>
                    )}
                    {c.cantidad_max_kg != null && (
                      <div><span className="text-neutral-500">Máximo por línea:</span> <span className="font-mono">{c.cantidad_max_kg} kg</span></div>
                    )}
                    {c.monto_min_factura != null && (
                      <div><span className="text-neutral-500">Monto mínimo factura:</span> <span className="font-mono">{fmtARS(c.monto_min_factura)}</span></div>
                    )}
                    {p.acumulable && (
                      <div className="col-span-full"><span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] uppercase font-bold rounded">Acumulable con otras promos</span></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button onClick={onClose} className="bg-[#6B1F24] hover:bg-[#571519]">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

